import IconCrossLarge from 'central-icons/IconCrossLarge';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Button, IconButton, RevelioTooltip } from '@revelio/ui';

import { useGlobalAIAssistant } from './global-ai-assistant-context';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import { GlobalAiConversationList } from './global-ai-conversation-list';
import styles from './global-ai-conversation-sidebar.module.css';

/**
 * Collapse state for the homepage history sidebar. Not persisted: every
 * visit starts with history closed, and history is opt-in per visit. Owned
 * by the PAGE (HomepageChat) rather than the sidebar because the toggle
 * renders in the page-title row, so the page needs the state for both.
 */
export function useConversationSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(true);
  const toggle = () => setCollapsed((prev) => !prev);
  return [collapsed, toggle];
}

/**
 * Sidebar-toggle glyph — always the open-state drawing, in both states.
 * Direction is signalled by the tooltip and the directional resize cursor on
 * the wrapping button, not by the icon. Local SVG because every
 * central-icons glyph carries the family's 2px corner radius. Exported for
 * the drawer's history toggle, which uses the same glyph.
 */
export function SidebarToggleGlyph() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect
        x="4"
        y="4"
        width="16"
        height="16"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M9 4V20" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** The sidebar show/hide toggle rendered in the page-title row. */
export function GlobalAiConversationSidebarToggle({
  collapsed,
  onToggle,
  hideCursorResize = false,
}: {
  collapsed: boolean;
  onToggle: () => void;
  hideCursorResize?: boolean;
}) {
  const label = collapsed ? 'Show chat history' : 'Hide chat history';
  const resizeCursor = collapsed ? 'e' : 'w';
  return (
    <RevelioTooltip label={label} placement="bottom">
      <IconButton
        aria-label={label}
        // Static open-state glyph in both states (no flip). Direction cue is
        // the ChatGPT-style resize cursor: the OS left-right arrow whose
        // solid head points the way the sidebar will move — out (east) when
        // collapsed, back in (west) when open.
        icon={<SidebarToggleGlyph />}
        data-resize-cursor={hideCursorResize ? undefined : resizeCursor}
        size="md"
        shape="rounded"
        className={styles.iconBtn}
        onClick={onToggle}
        data-testid={
          collapsed ? 'assistant-sidebar-expand' : 'assistant-sidebar-collapse'
        }
      />
    </RevelioTooltip>
  );
}

/**
 * Conversation-history sidebar body for the assistant homepage (Figma
 * Dashboard 2026, 717-19660 dark / 717-20200 light): New Chat + the shared
 * conversation list. The "AI Chat" title and the collapse toggle live
 * in the page-title row (HomepageChat), which also decides the
 * collapsed state. The sidebar stays MOUNTED while collapsed and animates
 * its width shut (same snappy style as the main nav's hover-expand), with
 * `visibility` flipping after the slide so the exit stays paintable.
 */
export function GlobalAiConversationSidebar({
  collapsed,
  onSelect,
  onClose,
  hideNewChatButton = false,
}: {
  collapsed: boolean;
  onSelect?: () => void;
  onClose?: () => void;
  hideNewChatButton?: boolean;
}) {
  const { persistence } = useGlobalAIAssistant();
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);
  // Match the drawer's New Chat / chat input: also block while a report offer
  // is launching, so New Chat can't reset the session out from under a launch
  // that's still writing back to the current assistant state.
  const isReportOfferLaunching = useGlobalAiEngineStore(
    (s) => s.reportOfferLaunchingKey !== null
  );

  // `inert` pulls the collapsed sidebar out of the tab order and a11y tree
  // SYNCHRONOUSLY — the CSS `visibility: hidden` only lands after the 30ms
  // width snap, and a Tab during that window could otherwise focus a
  // clipped, invisible row (review P2). Set imperatively — React 18 has no
  // `inert` prop — mirroring the drawer's chat-column pattern.
  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sidebarRef.current;
    if (el) {
      el.inert = collapsed;
    }
  }, [collapsed]);

  return (
    <div
      ref={sidebarRef}
      className={classNames(
        `${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`,
        styles.assistantConversationSidebar
      )}
      data-testid="assistant-conversation-sidebar"
    >
      {/* Plain conventional ghost per review — deliberately NOT restyled to
          the mock's gray fill: the DS has no filled-surface variant and the
          reviewer chose following the design system over matching the mock
          (option 2 in the thread). Only layout is customized (left-aligned
          label, input-matched height). */}
      <div
        className={styles.sidebarActions}
        data-hide-new-chat={hideNewChatButton}
      >
        {!hideNewChatButton && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<IconPlusSmall size={16} />}
            className={styles.newChatBtn}
            onClick={() => persistence.newChat()}
            disabled={isTurnRunning || isReportOfferLaunching}
            data-testid="assistant-new-chat"
          >
            New Chat
          </Button>
        )}
        {onClose && (
          <IconButton
            aria-label="Close chat history"
            icon={<IconCrossLarge size={12} />}
            size="md"
            shape="rounded"
            className={styles.iconBtn}
            onClick={onClose}
            data-testid="assistant-sidebar-close"
          />
        )}
      </div>
      <GlobalAiConversationList onSelected={onSelect} />
    </div>
  );
}
